import { CONVERSATION_GROUPING } from 'Constants/enums';
import { MobXProviderContext } from 'mobx-react';
import { ContactModelBase } from 'Models/ContactModel';
import { IParticipantCreate } from 'Models/ParticipantModel';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { bugsnagClient } from 'Utils/logUtils';
import { convertContactsToParticipants } from '../utils';

export interface CreateConversationError {
  type: 'GROUP_NAME_EXISTS' | 'GENERAL_ERROR' | 'VALIDATION_ERROR';
  error: any;
  message?: string;
}

export interface CreateNamedGroupParams {
  participants: IParticipantCreate[];
  topic: string;
  description?: string;
  grouping?: CONVERSATION_GROUPING;
  onError?: (error: CreateConversationError) => void;
}

export interface CreateAdHocGroupParams {
  participants: IParticipantCreate[];
  onError?: (error: CreateConversationError) => void;
}

export interface CheckExistingAdHocGroupParams {
  participants: IParticipantCreate[];
  onError?: (error: CreateConversationError) => void;
}

interface ApiError {
  response?: {
    status?: number;
  };
}

export const useConversationManager = () => {
  const { setPreviewConversationId, setIsAdHocGroupCreation } =
    useConversationStore();
  const navigate = useNavigate();
  const { conversation, person } =
    useContext<RootStoreProps>(MobXProviderContext);

  /**
   * Validates named group parameters
   */
  const validateNamedGroupParams = useCallback(
    (participants: IParticipantCreate[], topic: string) => {
      if (!participants.length) {
        throw new Error('At least one participant is required');
      }

      if (!topic?.trim()) {
        throw new Error('Group topic is required');
      }
    },
    []
  );

  /**
   * Determines channel type based on participants
   */
  const determineChannelType = useCallback(
    (participants: IParticipantCreate[]): 'phone' | 'person' => {
      const hasPersonId = participants.some((p) => p.personId);
      const hasPhone = participants.some((p) => p.phone);

      if (hasPersonId && hasPhone) return 'person';
      if (hasPersonId) return 'person';
      if (hasPhone) return 'phone';
      return 'person';
    },
    []
  );

  /**
   * Handles named group errors
   */
  const handleNamedGroupError = useCallback(
    (error: unknown, onError?: (error: CreateConversationError) => void) => {
      const apiError = error as ApiError;
      if (apiError.response?.status === 409) {
        // 409 Conflict (group name exists) - don't clear preview, just show error
        if (onError) {
          onError({ type: 'GROUP_NAME_EXISTS', error });
        }
        conversation.clearNameTaken();
      } else {
        // Clear preview for other errors
        setPreviewConversationId(null);
        bugsnagClient.notify(new Error(error as string), (event) => {
          event.severity = 'error';
          event.context = 'useConversationManager';
          event.addMetadata('custom', { function: 'createNamedGroup' });
        });

        if (onError) {
          onError({ type: 'GENERAL_ERROR', error });
        }
      }
    },
    [conversation, setPreviewConversationId]
  );

  /**
   * Creates a one-to-one conversation
   */
  const createOneToOne = useCallback(
    async ({ participants }: { participants: IParticipantCreate[] }) => {
      setPreviewConversationId(null);

      if (participants.length !== 1) {
        throw new Error(
          'One-to-one conversation requires exactly one participant'
        );
      }

      const participant = participants[0];

      if (!participant.personId && !participant.phone) {
        throw new Error('Participant must have either personId or phone');
      }

      try {
        const result = await conversation.loadOrCreateConversationWithPost(
          participant.personId,
          participant.phone,
          undefined,
          false
        );

        if (result?.data?.id) {
          // Track one-to-one conversation creation
          pushToGTMDataLayer('new_conversation_1:1', {
            conversationId: result.data.id.toString(),
          });

          setPreviewConversationId(result.data.id.toString());
          return result.data;
        }

        setPreviewConversationId(null);
        return null;
      } catch (error) {
        console.error('[conversation] createOneToOne:error', error);
        setPreviewConversationId(null);
        throw error;
      }
    },
    [conversation, setPreviewConversationId]
  );

  /**
   * Creates a named group conversation
   */
  const createNamedGroup = useCallback(
    async ({
      participants,
      topic,
      description = '',
      grouping = 'Channel' as CONVERSATION_GROUPING,
      onError,
    }: CreateNamedGroupParams) => {
      // Don't clear preview immediately - keep showing existing conversation

      try {
        validateNamedGroupParams(participants, topic);

        const result = await conversation.createConversationPost(
          topic,
          description,
          grouping,
          ...participants
        );

        if (result?.data?.id) {
          // Track named group conversation creation
          pushToGTMDataLayer('new_conversation_group', {
            conversationId: result.data.id.toString(),
            groupName: topic,
          });

          // Only now update to the new group
          setPreviewConversationId(result.data.id.toString());
          void navigate(`/chat/conversations/${result.data.id}/menu`);
          return result.data;
        }

        // Only clear preview if creation failed
        setPreviewConversationId(null);
        return null;
      } catch (error) {
        // Only clear preview on error
        handleNamedGroupError(error, onError);
        throw error;
      }
    },
    [
      conversation,
      navigate,
      setPreviewConversationId,
      validateNamedGroupParams,
      handleNamedGroupError,
    ]
  );

  /**
   * Creates an ad-hoc group conversation
   */
  const createAdHocGroup = useCallback(
    async ({ participants, onError }: CreateAdHocGroupParams) => {
      setPreviewConversationId(null);

      if (!participants.length) {
        throw new Error('At least one participant is required');
      }

      setIsAdHocGroupCreation(true);

      const channelType = determineChannelType(participants);
      conversation.setChannelInfoDetails('', 'new', channelType);

      const adHocGroupParticipants = participants
        .map((participant) => {
          if (participant.personId) {
            const personPbo = person.loadPersonByIdGetIfMissingGet(
              participant.personId.toString()
            );
            return personPbo?.case({
              fulfilled: (resp) => resp,
            });
          } else if (participant.phone) {
            return new ContactModelBase({
              id: participant.phone,
              name: participant.phone,
              phoneNumber: participant.phone,
              contactType: 'Unknown',
              created: '',
              searchableType: 'SearchableDetailsContact',
            });
          }
          return null;
        })
        .filter(Boolean);

      conversation.setAdHocGrouParticipants(adHocGroupParticipants);

      const participantStrings = participants
        .map((participant) => {
          if (participant.personId) {
            return participant.personId.toString();
          } else if (participant.phone) {
            return participant.phone.replace(/[^0-9+]/g, '');
          }
          return '';
        })
        .filter(Boolean);

      try {
        const result = await conversation.loadOrCreateAdHocGroup(
          participantStrings,
          false
        );

        if (result?.data?.id) {
          // Track ad-hoc group conversation creation
          pushToGTMDataLayer('new_conversation_ad-hoc_group', {
            conversationId: result.data.id.toString(),
            participantCount: participants.length,
          });

          setPreviewConversationId(result.data.id.toString());
          return result.data;
        }
        setPreviewConversationId(null);
        setIsAdHocGroupCreation(false);
        return null;
      } catch (error: unknown) {
        console.error('[conversation] createAdHocGroup:error', error);
        setPreviewConversationId(null);

        if (onError) {
          onError({ type: 'GENERAL_ERROR', error });
        }
        throw error;
      }
    },
    [
      setPreviewConversationId,
      setIsAdHocGroupCreation,
      determineChannelType,
      conversation,
      person,
    ]
  );

  /**
   * Checks if an ad-hoc group exists for the given participants (only for internal contacts)
   * This is used to preview existing groups without creating new ones
   */
  const checkExistingAdHocGroup = useCallback(
    async ({ participants, onError }: CheckExistingAdHocGroupParams) => {
      // Only check for internal contacts (personId exists)
      const hasInternalContacts = participants.some((p) => p.personId);
      if (!hasInternalContacts) {
        // This case is for external-only groups, which should be created directly.
        // Enable ad-hoc creation for external contacts
        setIsAdHocGroupCreation(true);
        return null;
      }

      const channelType = determineChannelType(participants);
      conversation.setChannelInfoDetails('', 'new', channelType);

      const adHocGroupParticipants = participants
        .map((participant) => {
          if (participant.personId) {
            const personPbo = person.loadPersonByIdGetIfMissingGet(
              participant.personId.toString()
            );
            return personPbo?.case({
              fulfilled: (resp) => resp,
            });
          }
          return null;
        })
        .filter(Boolean);

      conversation.setAdHocGrouParticipants(adHocGroupParticipants);

      const participantStrings = participants
        .map((participant) => {
          if (participant.personId) {
            return participant.personId.toString();
          }
          return '';
        })
        .filter(Boolean);

      try {
        // Use onlyGet: true to check if group exists without creating
        const result = await conversation.loadOrCreateAdHocGroup(
          participantStrings,
          true
        );

        if (result?.data?.id) {
          setPreviewConversationId(result.data.id.toString());
          return result.data;
        }

        // If no existing group is found, clear preview and enable ad-hoc creation
        setPreviewConversationId(null);
        setIsAdHocGroupCreation(true);
        return null;
      } catch (error: unknown) {
        console.error('[conversation] checkExistingAdHocGroup:error', error);
        setPreviewConversationId(null);
        // Enable ad-hoc creation on error as well
        setIsAdHocGroupCreation(true);
        // Let the caller handle the error and state.
        if (onError) onError({ type: 'GENERAL_ERROR', error });
        return null;
      }
    },
    [
      setPreviewConversationId,
      setIsAdHocGroupCreation,
      determineChannelType,
      conversation,
      person,
    ]
  );

  return {
    createOneToOne,
    createNamedGroup,
    createAdHocGroup,
    checkExistingAdHocGroup,
    convertContactsToParticipants,
  };
};
